import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContractList() {
  const { user } = useContext(AuthContext);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterContracts();
  }, [contracts, searchTerm, statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      const [contractsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/users`)
      ]);

      setContracts(contractsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const filterContracts = () => {
    let filtered = contracts;

    if (searchTerm) {
      filtered = filtered.filter(contract =>
        contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contract.supplier_name && contract.supplier_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contract.location && contract.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
        contract.type_contract.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(contract => contract.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(contract => contract.type_contract === typeFilter);
    }

    setFilteredContracts(filtered);
  };

  const deleteContract = async (contractId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;

    try {
      await axios.delete(`${API}/contracts/${contractId}`);
      setContracts(contracts.filter(c => c.id !== contractId));
      toast.success('Contrat supprimé avec succès');
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status) => {
    const configs = {
      'actif': { 
        label: 'Actif',
        className: 'status-actif',
        icon: '●'
      },
      'expiré': { 
        label: 'Expiré',
        className: 'status-expiré',
        icon: '⚠'
      },
      'en_renouvellement': { 
        label: 'Renouvellement',
        className: 'status-en_renouvellement',
        icon: '⟲'
      }
    };

    const config = configs[status] || configs['actif'];
    return (
      <span className={`status-badge ${config.className}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getUniqueTypes = () => {
    const types = [...new Set(contracts.map(c => c.type_contract))];
    return types.sort();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-list-page">
      {/* Header avec filtres intégrés */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary">Gestion des Contrats</h1>
            <p className="text-text-muted mt-1">
              {filteredContracts.length} contrat(s) trouvé(s)
              <span className="font-mono text-xs ml-2 px-2 py-1 bg-bg-muted rounded">
                Query: {contracts.length} records
              </span>
            </p>
          </div>
          <Link to="/contracts/new">
            <Button className="btn-primary" data-testid="add-contract-button">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nouveau contrat
            </Button>
          </Link>
        </div>

        {/* Filtres compacts en ligne */}
        <Card className="border-l-4 border-l-primary">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                </svg>
                <span className="text-sm font-medium text-text-primary">Filtres:</span>
              </div>
              
              <div className="flex-1 min-w-64">
                <Input
                  placeholder="Rechercher... (nom, fournisseur, localisation, type)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                  className="form-input h-8"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="status-filter"
                className="h-8 w-auto min-w-32 px-3 py-1 text-sm border border-border-medium rounded bg-bg-surface text-text-primary"
                style={{color: 'var(--text-primary)'}}
              >
                <option value="all">Tous statuts</option>
                <option value="actif">● Actif</option>
                <option value="en_renouvellement">⟲ Renouvellement</option>
                <option value="expiré">⚠ Expiré</option>
              </select>
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="type-filter"
                className="h-8 w-auto min-w-40 px-3 py-1 text-sm border border-border-medium rounded bg-bg-surface text-text-primary"
                style={{color: 'var(--text-primary)'}}
              >
                <option value="all">Tous types</option>
                {getUniqueTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              
              {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                  data-testid="clear-filters-button"
                  className="btn-ghost h-8 px-3 text-xs"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contracts Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Database Records
            </CardTitle>
            <span className="text-xs text-text-muted font-mono">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredContracts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table" data-testid="contracts-table">
                <thead>
                  <tr>
                    <th className="w-1/4">Contrat</th>
                    <th className="w-1/6">Fournisseur</th>
                    <th className="w-1/8">Type</th>
                    <th className="w-1/8">Statut</th>
                    <th className="w-1/8">Échéance</th>
                    <th className="w-1/8">Montant</th>
                    <th className="w-1/6">Assigné à</th>
                    <th className="w-20 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map((contract, index) => (
                    <tr key={contract.id} data-testid={`contract-row-${contract.id}`} className="group">
                      <td>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-text-muted">#{index.toString().padStart(2, '0')}</span>
                            <span className="font-semibold text-text-primary" data-testid={`contract-name-${contract.id}`}>
                              {contract.name}
                            </span>
                          </div>
                          {contract.location && (
                            <div className="text-xs text-text-muted mt-1">
                              📍 {contract.location}
                            </div>
                          )}
                          <div className="text-xs text-text-muted font-mono mt-1">
                            ID: {contract.id.slice(-8)}
                          </div>
                        </div>
                      </td>
                      <td className="text-text-secondary" data-testid={`contract-supplier-${contract.id}`}>
                        {contract.supplier_name || contract.supplier || '-'}
                      </td>
                      <td className="text-text-secondary text-sm" data-testid={`contract-type-${contract.id}`}>
                        {contract.type_contract}
                      </td>
                      <td data-testid={`contract-status-${contract.id}`}>
                        {getStatusBadge(contract.status)}
                      </td>
                      <td className="text-text-secondary font-mono text-sm" data-testid={`contract-end-date-${contract.id}`}>
                        {formatDate(contract.end_date)}
                      </td>
                      <td className="font-semibold text-text-primary" data-testid={`contract-amount-${contract.id}`}>
                        {formatCurrency(contract.amount)}
                      </td>
                      <td className="text-text-secondary" data-testid={`contract-assigned-${contract.id}`}>
                        {contract.assigned_user_name || (
                          <span className="text-text-muted italic text-sm">Non assigné</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link to={`/contracts/${contract.id}/edit`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-contract-${contract.id}`}
                              className="btn-ghost text-primary hover:text-primary/80 p-2"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteContract(contract.id)}
                            data-testid={`delete-contract-${contract.id}`}
                            className="btn-ghost text-error hover:text-error/80 p-2"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-contracts-message">
              <div className="font-mono text-6xl text-text-subtle mb-4">404</div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">Aucun contrat trouvé</h3>
              <p className="text-text-muted mb-4">
                {contracts.length === 0 
                  ? "Base de données vide - Créez votre premier contrat" 
                  : "Aucun résultat pour cette requête"
                }
              </p>
              {contracts.length === 0 && (
                <Link to="/contracts/new">
                  <Button className="btn-primary" data-testid="create-first-contract-from-list">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Créer le premier contrat
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Footer - Geek touch */}
      {filteredContracts.length > 0 && (
        <div className="flex items-center justify-between text-sm text-text-muted bg-bg-subtle p-3 rounded border-l-4 border-l-primary/50">
          <div className="font-mono">
            SELECT * FROM contracts WHERE status IN ({statusFilter === 'all' ? 'ALL' : statusFilter}) 
            {searchTerm && ` AND name LIKE '%${searchTerm}%'`}
            {typeFilter !== 'all' && ` AND type='${typeFilter}'`}
          </div>
          <div className="flex items-center gap-4">
            <span>Résultats: <strong>{filteredContracts.length}</strong></span>
            <span className="font-mono">
              Σ {formatCurrency(filteredContracts.reduce((sum, c) => sum + c.amount, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}